import shoppingCartUrl from "@assets/images/shopping-cart.svg";
import {
  Badge,
  Button,
  LinearProgress,
  useMediaQuery
} from "@material-ui/core";
import { useUser } from "@saleor/auth";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { Zone } from "@saleor/auth/types";
import { draftOrder, shoppingCart } from "@saleor/createOrder/urls";
import { useCustomerBlockContext } from "@saleor/customerBlock/hook/useCustomerBlockContext";
import {
  PermissionEnum,
  useCartTotalCheckoutQuery,
  useCustomerCartTotalsQuery,
  useTotalCountExportCartQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import {
  makeStyles,
  SaleorTheme,
  SidebarDrawer,
  useActionBar,
  useBacklink
} from "@saleor/macaw-ui";
import { staffMemberDetailsUrl } from "@saleor/staff/urls";
import classNames from "classnames";
import React, {
  createRef,
  useContext,
  useEffect,
  useImperativeHandle
} from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { LayoutContext } from "src/context/LayoutContext";
import useRouter from "use-react-router";

import Container from "../Container";
import ErrorPage from "../ErrorPage";
import HeadColored from "../HeadColored";
import MyTypography from "../MyTypography";
import PlainLayout from "../PlainLayout";
import Sidebar from "../Siderbar";
import UserChip from "../UserChip";
import AppHeaderContext from "./AppHeaderContext";
import { appLoaderHeight, hideLayoutPage } from "./consts";
import useMenuStructure from "./menuStructure";
import { isMenuActive } from "./utils";

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  theme => ({
    appAction: {
      [theme.breakpoints.down("sm")]: {
        left: 0,
        width: "100%"
      },
      bottom: 0,
      gridColumn: 2,
      position: "sticky",
      zIndex: 10
    },
    appLoader: {
      height: appLoaderHeight,
      marginBottom: theme.spacing(4),
      zIndex: 1201,
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%"
    },
    appLoaderPlaceholder: {
      height: appLoaderHeight,
      marginBottom: theme.spacing(1),
      width: "100%",
      position: "fixed",
      top: 0,
      left: 0
    },
    content: {
      flex: 1,
      [theme.breakpoints.up("md")]: {
        width: 0 // workaround for flex children width expansion affected by their contents
      },
      position: "relative"
    },
    darkThemeSwitch: {
      [theme.breakpoints.down("sm")]: {
        marginRight: theme.spacing(1)
      },
      marginRight: theme.spacing(2)
    },
    header: {
      display: props => (props.fullScreen ? "none" : "grid"),
      gridTemplateAreas: `"headerAnchor headerToolbar"`,
      [theme.breakpoints.down("sm")]: {
        gridTemplateAreas: `"headerToolbar"
        "headerAnchor"`
      },
      marginTop: "1.6rem",
      marginBottom: "3.2rem"
    },
    headerAnchor: {
      gridArea: "headerAnchor"
    },
    headerToolbar: {
      display: "flex",
      gridArea: "headerToolbar",
      height: 40,
      [theme.breakpoints.down("sm")]: {
        height: "auto"
      }
    },
    root: {
      [theme.breakpoints.up("md")]: {
        display: "flex"
      },
      width: `100%`
    },
    spacer: {
      flex: 1
    },
    userBar: {
      alignItems: "center",
      display: "flex"
    },
    cartCount: {
      display: "flex",
      alignItems: "center",
      marginRight: "1.6rem",
      borderRight: `solid 1px ${theme.palette.purple.main}`,
      paddingRight: "2.4rem",
      "& .MuiBadge-root": {
        cursor: "pointer"
      },
      "& .MuiBadge-badge": {
        height: 18,
        width: 18,
        minWidth: 18,
        bottom: "3px",
        fontSize: 14,
        fontFamily: "inherit",
        fontWeight: 700
      },
      "& .MuiTypography-root": {
        color: theme.palette.purple.main
      }
    },
    buttonCountHasItem: {
      padding: "1rem 1.5rem 1.5rem 1rem"
    },
    buttonCountNoItem: {
      padding: "1rem"
    },
    buttonCount: {
      minWidth: 40,
      transition: "none",
      marginLeft: "14px",
      "&:hover": {
        background: theme.palette.background.default,
        mixBlendMode: "multiply"
      },
      "&:active": {
        background: theme.palette.background.default,
        mixBlendMode: "multiply",
        outline: "3px solid rgba(1, 178, 237, 0.24)"
      }
    },
    view: {
      marginLeft: 0,
      paddingBottom: theme.spacing(),
      [theme.breakpoints.up("sm")]: {
        paddingBottom: theme.spacing(3)
      }
    },
    viewContainer: {
      // minHeight: `calc(100vh - ${appLoaderHeight + 72}px - ${theme.spacing(4)})`
      minHeight: `calc(100vh - ${appLoaderHeight + 14}px)`
    },
    cartIcon: {},
    badgeCount: {
      "& .MuiBadge-badge": {
        padding: "1rem"
      }
    }
  }),
  {
    name: "AppLayout"
  }
);

interface AppLayoutProps {
  children: React.ReactNode;
}

export const changeRole = createRef<{
  setRole: React.Dispatch<React.SetStateAction<Zone>>;
}>();

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [appState, dispatchAppState] = useAppState();
  const classes = useStyles({ fullScreen: appState.fullScreen });
  const { anchor: appActionAnchor } = useActionBar();
  const appHeaderAnchor = useBacklink();
  const layoutContext = useContext(LayoutContext);
  const history = useHistory();

  const {
    logout,
    user,
    setZone,
    listCompany,
    setCompany,
    company,
    soldToId,
    setSoldToId
  } = useUser();

  const navigate = useNavigator();
  const intl = useIntl();
  const { location } = useRouter();
  const [bodyPaddingRight, setBodyPaddingRight] = React.useState(0);
  const [role, setRole] = React.useState<Zone>(null);
  const {
    customerBlockStatus,
    onOpenCustomerBlockDialog
  } = useCustomerBlockContext();

  useImperativeHandle(changeRole, () => ({ setRole }));

  const permissions = useUserPermissions();

  const extensionsPermissionExport = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
  );

  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );
  const exceptPermissionNotRefCreateOrderIcon = permissions?.find(
    perm =>
      perm.code === PermissionEnum.CREATE_ORDER_CUSTOMER ||
      perm.code === PermissionEnum.CREATE_ORDER_DOMESTIC ||
      perm.code === PermissionEnum.CREATE_ORDER_EXPORT
  );

  const shoppingDomesticPackagingPermission = permissions?.find(
    perm => perm.code === PermissionEnum.CREATE_ORDER_DOMESTIC_PACKAGING
  );

  useEffect(() => {
    if (!extensionsPermissionDomestic && extensionsPermissionExport) {
      setRole(Zone.Export);
    } else {
      setRole(Zone.Domestic);
    }
    const prevRole = sessionStorage.getItem("previousRole");
    if (
      extensionsPermissionDomestic &&
      extensionsPermissionExport &&
      prevRole === Zone.Export
    ) {
      // sessionStorage.removeItem("previousRole");
      // navigate("/", {replace: true});
      setRole(prevRole);
    }
  }, [extensionsPermissionExport, extensionsPermissionDomestic]);

  useEffect(() => {
    const localtion = history.location;
    const checkUrlNotRef =
      localtion?.pathname?.includes("not-ref-contract") ||
      localtion?.pathname?.includes("not-ref-change-order");

    if (!checkUrlNotRef) {
      const searchString = localtion?.search;
      if (searchString.includes("mode=edit")) {
        history.replace({
          search: "mode=edit"
        });
      } else {
        history.replace({
          search: ""
        });
      }
    }
    setZone(role);
  }, [role]);

  const onSaveAndDrafClick = () => {
    navigate(draftOrder, { replace: true });
  };

  const isMdUp = useMediaQuery((theme: SaleorTheme) =>
    theme.breakpoints.up("md")
  );

  const isHomePage = location.pathname === "/";

  const [menuStructure, handleMenuItemClick] = useMenuStructure(intl, user);
  const activeMenu = menuStructure.find(menuItem =>
    isMenuActive(location.pathname, menuItem)
  )?.id;

  const menuStructureFilterd = menuStructure.filter(
    menuItem => menuItem.id !== "createOrderNotRef"
  );

  const { data: cartDataCSDomestic } = useCartTotalCheckoutQuery({
    displayLoader: true,
    skip: !extensionsPermissionDomestic
  });

  const { data: cartDataCustomer } = useCustomerCartTotalsQuery({
    variables: {
      soldToId
    },
    displayLoader: true,
    skip: user.isStaff
  });

  const { data: cartDataCSExport } = useTotalCountExportCartQuery({
    displayLoader: true,
    skip: !extensionsPermissionExport
  });

  useEffect(() => {
    if (cartDataCSDomestic && role === Zone.Domestic) {
      const orderCount =
        cartDataCSDomestic.totalContractCheckouts?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  }, [cartDataCSDomestic, role]);

  useEffect(() => {
    if (cartDataCustomer && role === Zone.Domestic) {
      const orderCount =
        cartDataCustomer.customerCartTotals?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  }, [cartDataCustomer, role]);

  useEffect(() => {
    if (cartDataCSExport && role === Zone.Export) {
      const orderCount = cartDataCSExport.exportCarts?.totalCartItem || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  }, [cartDataCSExport, role]);

  const reloadWindow = () => {
    window.location.reload();
  };

  const handleErrorBack = () => {
    navigate("/", { replace: true });
    dispatchAppState({
      payload: {
        error: null
      },
      type: "displayError"
    });
    reloadWindow();
  };

  const target = document.getElementById("body-app");

  // Check change body style
  const observer = new MutationObserver(mutations => {
    observer.disconnect();

    if (mutations.length) {
      // @ts-ignore
      const bodyPaddingRightStr = mutations[0].target.style["padding-right"];
      const _bodyPaddingRight = bodyPaddingRightStr
        ? bodyPaddingRightStr.slice(0, -2)
        : 0;

      setBodyPaddingRight(_bodyPaddingRight);
    }
    if (target) {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ["style"]
      });
    }
  });

  useEffect(() => {
    if (target) {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ["style"]
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    dispatchAppState({
      payload: {
        bodyPaddingRight: parseInt(String(bodyPaddingRight), 10)
      },
      type: "setBodyPaddingRight"
    });
  }, [bodyPaddingRight]);

  const handleChangeRole = role => {
    sessionStorage.setItem("previousRole", role);
    setRole(role);
  };

  const handleChangeCompany = company => {
    setCompany(company);
  };

  const handleChangeSoldToId = company => {
    setSoldToId(company);
  };

  const navigateToShoppingCartHandler = () => {
    // Check Customer block!!!
    if (customerBlockStatus) {
      onOpenCustomerBlockDialog();
    } else {
      navigate(shoppingCart);
    }
  };

  const renderSoldToName = () => {
    if (!user.isStaff && listCompany.length > 0 && company) {
      const filteredCompany = listCompany.filter(
        item => item.soldToCode === company
      );

      return (
        <MyTypography data-testid="soldto-name" variant="subtitle2">
          {filteredCompany[0]?.soldToName}
        </MyTypography>
      );
    }
  };

  useEffect(() => {
    const SOLD_TO_ID = window.localStorage.getItem("SOLD_TO_ID");

    if (SOLD_TO_ID && !user.isStaff) {
      const filteredCompany = listCompany.filter(
        comp => comp.id === SOLD_TO_ID
      );

      if (filteredCompany.length > 0) {
        setSoldToId(filteredCompany[0]?.id);
        setCompany(filteredCompany[0]?.soldToCode);
        dispatchAppState({
          payload: {
            showPoUpload: filteredCompany[0]?.showPoUpload
          },
          type: "showPoUpload"
        });
      }
    }
  }, [soldToId]);

  return (
    <>
      {layoutContext.layoutType === "app" && (
        <AppHeaderContext.Provider value={appHeaderAnchor}>
          <div className={classes.root}>
            {isMdUp && !isHomePage && hideLayoutPage(location.pathname) && (
              <Sidebar
                onLogout={logout}
                activeId={activeMenu}
                menuItems={
                  shoppingDomesticPackagingPermission &&
                  !exceptPermissionNotRefCreateOrderIcon
                    ? menuStructure
                    : menuStructureFilterd
                }
                onMenuItemClick={handleMenuItemClick}
              />
            )}
            <div className={classes.content}>
              {appState.loading ? (
                <LinearProgress className={classes.appLoader} color="primary" />
              ) : (
                <div className={classes.appLoaderPlaceholder} />
              )}

              <div className={classes.viewContainer}>
                {isHomePage && (
                  <div
                    style={{
                      maxWidth: "100%",
                      overflow: "hidden"
                    }}
                  >
                    <HeadColored />
                  </div>
                )}

                <div>
                  <Container>
                    <div className={classes.header}>
                      <div
                        className={classes.headerAnchor}
                        ref={appHeaderAnchor}
                      />
                      <div className={classes.headerToolbar}>
                        {!isMdUp &&
                          !isHomePage &&
                          hideLayoutPage(location.pathname) && (
                            <SidebarDrawer
                              menuItems={
                                shoppingDomesticPackagingPermission &&
                                !exceptPermissionNotRefCreateOrderIcon
                                  ? menuStructure
                                  : menuStructureFilterd
                              }
                              onMenuItemClick={handleMenuItemClick}
                            />
                          )}
                        <div className={classes.spacer} />
                        {hideLayoutPage(location.pathname) && (
                          <div
                            data-testid="userBar"
                            className={classes.userBar}
                          >
                            <div className={classes.cartCount}>
                              {renderSoldToName()}
                              {extensionsPermissionExport ||
                              extensionsPermissionDomestic ? (
                                <MyTypography
                                  data-testid="role-name"
                                  variant="subtitle2"
                                >
                                  {role === Zone.Export
                                    ? intl.formatMessage({
                                        defaultMessage: "Export"
                                      })
                                    : intl.formatMessage({
                                        defaultMessage: "Domestic"
                                      })}
                                </MyTypography>
                              ) : null}
                              <Button
                                id="cartButton"
                                data-testid="cart-button"
                                className={classNames(
                                  classes.buttonCount,
                                  appState.orderCount > 0
                                    ? classes.buttonCountHasItem
                                    : classes.buttonCountNoItem
                                )}
                                onClick={navigateToShoppingCartHandler}
                              >
                                <Badge
                                  id="cartButtonBadgeCount"
                                  className={classes.badgeCount}
                                  badgeContent={appState.orderCount}
                                  color={"primary"}
                                  anchorOrigin={{
                                    horizontal: "right",
                                    vertical: "bottom"
                                  }}
                                >
                                  <SVG
                                    className={classes.cartIcon}
                                    src={shoppingCartUrl}
                                  />
                                </Badge>
                              </Button>
                            </div>
                            <UserChip
                              user={user}
                              onLogout={logout}
                              onSaveAndDrafClick={onSaveAndDrafClick}
                              role={role}
                              company={company}
                              setSoldToId={handleChangeSoldToId}
                              setCompany={handleChangeCompany}
                              setRole={handleChangeRole}
                              listCompany={listCompany}
                              onProfileClick={() =>
                                navigate(staffMemberDetailsUrl(user?.id))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Container>
                </div>
                <main className={classes.view}>
                  {appState.error
                    ? appState.error.type === "unhandled" && (
                        <ErrorPage
                          id={appState.error.id}
                          onBack={handleErrorBack}
                          onRefresh={() => window.location.reload()}
                        />
                      )
                    : children}
                </main>
              </div>
              <div className={classes.appAction} ref={appActionAnchor} />
            </div>
          </div>
        </AppHeaderContext.Provider>
      )}
      {layoutContext.layoutType === "plain" && (
        <PlainLayout>{children}</PlainLayout>
      )}
      {layoutContext.layoutType === "none" && <main>{children}</main>}
    </>
  );
};

export default AppLayout;
